import { useMemo } from "react";
import { IdwTableContainer } from "@containers/common/idw-table-container/idw-table-container";
import { IdwTable } from "@containers/common/idw-table/idw-table";
import { PaginationContainer } from "@containers/common/pagination-container/pagination-container";
import { TableSettingsTableTypes } from "@enums/table-settings-table-types";
import { PolicyComponentAssociatedPolicy } from "@idw-types/policy-component-associated-policy";
import { useTableFilters } from "@hooks/use-table-filters";
import { sortStrings } from "@utils/string-util";
import { createColumnHelper, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, TableState, useReactTable } from "@tanstack/react-table";
import { useDispatch } from "react-redux";
import { handleSetColumnFiltersChange, handleSetPaginationChange, handleSetSortingChange } from "@utils/react-table/react-table-util";

type AssociatedPoliciesTableContainerProps = {
    associatedPolicies: Array<PolicyComponentAssociatedPolicy>;
    tableState: TableState;
}

type AssociatedPoliciesTableDataRow = PolicyComponentAssociatedPolicy & {
    relyingPartyName: string;
    workflowName: string;
    organizationName: string;
};

export const AssociatedPoliciesTableContainer = (props: AssociatedPoliciesTableContainerProps) => {
    const id = "associated-policies";
    const { associatedPolicies, tableState } = props;
    const dispatch = useDispatch();

    const associatedPoliciesData = useMemo(() => {
        return associatedPolicies.map((associatedPolicy) => ({
            ...associatedPolicy,
            relyingPartyName: associatedPolicy.relyingParty !== null ? associatedPolicy.relyingParty.name : "",
            workflowName: associatedPolicy.workflow !== null ? associatedPolicy.workflow.name : "",
            organizationName: associatedPolicy.organization !== null ? associatedPolicy.organization.name : "",

        }));
    }, [associatedPolicies]);

    const columnHelper = createColumnHelper<AssociatedPoliciesTableDataRow>();
    const columns = useMemo(() => ([
        columnHelper.accessor("serviceName", {
            header: "Service Name",
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.serviceName, b.original.serviceName)
        }),
        columnHelper.accessor("serviceApiKey", {
            header: "API Key",
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.serviceApiKey, b.original.serviceApiKey)
        }),
        columnHelper.accessor("workflowName", {
            header: "Workflow Name",
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.workflowName, b.original.workflowName)
        }),
        columnHelper.accessor("relyingPartyName", {
            header: "Relying Party",
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.relyingPartyName, b.original.relyingPartyName)
        }),
        columnHelper.accessor("organizationName", {
            header: "Organization",
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.organizationName, b.original.organizationName)
        })
    ]), [associatedPoliciesData]);

    const { filterTypes } = useTableFilters();

    const useTableResult = useReactTable({
        columns,
        data: associatedPoliciesData,
        state: tableState,
        filterFns: filterTypes,
        sortDescFirst: false,
        autoResetPageIndex: true,
        onSortingChange: (sortingOrUpdateFn) => handleSetSortingChange(sortingOrUpdateFn, TableSettingsTableTypes.POLICY_COMPONENT_DETAILS_ASSOCIATED_POLICY_COMPONENTS, tableState.sorting, dispatch),
        onColumnFiltersChange: (filtersOrUpdateFn) => handleSetColumnFiltersChange(filtersOrUpdateFn, TableSettingsTableTypes.POLICY_COMPONENT_DETAILS_ASSOCIATED_POLICY_COMPONENTS, tableState.columnFilters, dispatch),
        onPaginationChange: (paginationOrUpdateFn) => handleSetPaginationChange(paginationOrUpdateFn, TableSettingsTableTypes.POLICY_COMPONENT_DETAILS_ASSOCIATED_POLICY_COMPONENTS, tableState.pagination, dispatch),
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel()
    });

    return <IdwTableContainer>
        <IdwTable
            id={id}
            useTableResult={useTableResult}
            className={null}
            headerColumnKeyPrefix={id}
        />
        <PaginationContainer
            idPrefix={id}
            useTableResult={useTableResult}
            isPageHeight={true}
        />
    </IdwTableContainer>
}